import { ActivityLogType } from '@/types/audit-logs.types';

export const NETWORK_ERROR_CODE = 'ERR_NETWORK';
export const USER_UNAUTHORIZED = 401;

export enum UserTypes {
  ADMIN = 1,
  BOATER = 2,
  MARINA = 3,
  REGIONAL_OPERATOR = 4,
  YACHT_CLUB_ADMIN = 5,
  SUB_ADMIN = 6,
  DMO_ADMIN = 7,
}

export enum AccountingExportRunStatus {
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  SUCCEEDED = 'SUCCEEDED',
  PARTIALLY_SUCCEEDED = 'PARTIALLY_SUCCEEDED',
  FAILED = 'FAILED',
}

export enum AccountingEntryDirection {
  DEBIT = 'DEBIT',
  CREDIT = 'CREDIT',
}

export enum AccountingEntrySourceType {
  PAYMENT_TRANSACTION = 'PAYMENT_TRANSACTION',
  COMMISSION_OUTCOME = 'COMMISSION_OUTCOME',
  PSP_FEE = 'PSP_FEE',
  SETTLEMENT = 'SETTLEMENT',
  PENALTY_NO_SHOW = 'PENALTY_NO_SHOW',
  MANUAL = 'MANUAL',
}

export enum BookingType {
  BERTH = 'BERTH',
  ADDON = 'ADDON',
  SERVICE = 'SERVICE',
}

export enum HarborBookingType {
  RESERVATION = 'RESERVATION',
  PAYMENT_ONLY = 'PAYMENT_ONLY',
}

export enum TestimonialStatus {
  DRAFT = 1,
  PUBLISH = 2,
}

export enum PspFeeProvider {
  STRIPE = 1,
  PAYTRAIL = 2,
}

export enum SubscriptionType {
  BOATER = 1,
  HARBOR = 2,
  CLUB = 3,
  DMO = 4,
  SERVICE_PROVIDER = 5,
}
export enum CouponType {
  SUBSCRIPTION_BOATER = 1,
  SUBSCRIPTION_HARBOR = 2,
  SUBSCRIPTION_CLUB = 3,
  SUBSCRIPTION_DMO = 4,
  SUBSCRIPTION_SERVICE_PROVIDER = 5,
}

export enum CouponEligibilityRule {
  NONE = 0,
  CLUB_MEMBER_ONLY = 1,
}

export enum GroupByUnit {
  DAY = 'day',
  WEEK = 'week',
  MONTH = 'month',
}

export enum BillingCycle {
  MONTHLY = 1,
  YEARLY = 2,
  FIVE_DAYS = 3,
}

export enum PortWatchShiftStatus {
  OPEN = 1,
  FILLED = 2,
  COMPLETED = 3,
}

export enum SubscriptionStatus {
  ACTIVE = 1,
  INACTIVE = 2,
  EXPIRED = 3,
  CANCELLED = 4,
}

export enum Entity {
  HARBOR = 1,
  CLUB = 2,
  REGION = 3,
  ORGANIZATION = 4,
}

export enum UserRoles {
  ADMIN = 'admin',
  YACHT_CLUB_ADMIN = 'yacht_club_admin',
  REGIONAL_OPERATOR = 'regional_operator',
  DMO = 'dmo',
  DMO_STAFF = 'dmo_staff',
  BOATER = 'boater',
  MARINA = 'marina_manager',
  SUB_ADMIN = 'sub_admin',
  SERVICE_PROVIDER = 'service_provider',
  INSPECTOR = 'inspector',
}

export enum MembershipRequestStatus {
  PENDING = 0,
  APPROVED = 1,
  REJECTED = 2,
}

export enum Statuses {
  INACTIVE = 0,
  ACTIVE = 1,
  DELETED = 2,
  BLOCKED = 3,
  UNDER_RENOVATION = 4,
}

export enum HarborStatus {
  DRAFT = 1,
  PUBLISH = 2,
}

export enum POIStatus {
  ACTIVE = '1',
  INACTIVE = '2',
}

export enum RefundMode {
  REFUND_AS_POLICY = 'REFUND_AS_POLICY',
  NO_REFUND = 'NO_REFUND',
}

export enum ProductType {
  HARBOUR_BERTH = 'harbour_berth', //
  HARBOUR_SERVICE = 'harbour_service', //
  HARBOUR_ADDON = 'harbour_addon', //
  SAARISTO_SUBSCRIPTION = 'saaristo_subscription', //
  CLUB_RESOURCE = 'club_resource', //
  SERVICE_BOOKING = 'service_booking', //
  OTHER = 'other',
}

export enum BerthType {
  TRANSIENT = 1,
  SEASONAL = 2,
  YEARLY = 3,
}

export enum PaymentType {
  // Harbours / berths
  HARBOUR_BERTH = 'harbour_berth', // Main berth fee

  // Harbour-owned services / addons
  HARBOUR_SERVICE = 'harbour_service', // Generic harbour service (electricity, water, sauna, waste)
  HARBOUR_ADDON = 'harbour_addon', // Addon sold via booking addons (hourly/day slots etc.)

  // Third-party services sold through the platform (marketplace style)
  THIRD_PARTY_SERVICE = 'third_party_service',

  // Platform subscription & commission
  SAARISTO_SUBSCRIPTION = 'saaristo_subscription',
  SAARISTO_COMMISSION = 'saaristo_commission',

  // Club / resource orders
  CLUB_RESOURCE = 'club_resource', // resource (single)
  CLUB_RESOURCE_ORDER_ITEM = 'club_resource_order_item', // ordered item (e.g., key rental)

  // Service bookings (service provider bookings)
  SERVICE_BOOKING = 'service_booking',

  // Advertising / promotional placements (if you add later)
  ADVERTISING = 'advertising',

  // Catch-all for anything not yet modelled (avoid using except for backfill)
  OTHER = 'other',

  PENALTY_NO_SHOW = 'penalty_no_show',
  PSP_FEE = 'psp_fee',
}

export enum Seasons {
  SPRING = 'spring',
  SUMMER = 'summer',
  AUTUMN = 'autumn',
  WINTER = 'winter',
}

export enum TaxRegime {
  DOMESTIC_FI = 'DOMESTIC_FI',
  EU_OSS = 'EU_OSS',
  IOSS = 'IOSS',
  B2B_OTHER = 'B2B_OTHER',
}

export enum RegimeLogic {
  DOMESTIC_ONLY = 'DOMESTIC_ONLY',
  BY_CUSTOMER_COUNTRY = 'BY_CUSTOMER_COUNTRY',
  IOSS_ELIGIBLE = 'IOSS_ELIGIBLE',
}

export const roleOptions = [
  { value: 'Marina', label: 'Marina' },
  { value: 'Boater', label: 'Boater' },
  { value: 'Region Operator', label: 'Region Operator' },
  { value: 'Yacht Club Admin', label: 'Yacht Club Admin' },
  { value: 'DMO Admin', label: 'DMO Admin' },
];

export const statusOptions = [
  { value: 'Active', label: 'Active' },
  { value: 'Inactive', label: 'Inactive' },
];

// Constants for user types and activity types
export const userTypeMap: { [key: number]: string } = {
  1: 'Admin',
  2: 'Boater',
  3: 'Marina',
  4: 'Region Operator',
  5: 'Yacht Club Admin',
  6: 'Sub Admin',
  7: 'DMO Admin',
};

export enum SettlementRunStatus {
  DRAFT = 'DRAFT',
  PENDING = 'PENDING',
  RUNNING = 'RUNNING',
  FINALIZED = 'FINALIZED',
  FAILED = 'FAILED',
}

export enum SettlementActorType {
  HARBOR = 'HARBOR',
  SERVICE_PROVIDER = 'SERVICE_PROVIDER',
}

export enum SettlementPayoutStatus {
  ELIGIBLE = 'ELIGIBLE',
  BLOCKED_KYC = 'BLOCKED_KYC',
  BELOW_THRESHOLD = 'BELOW_THRESHOLD',
  NEGATIVE_BALANCE = 'NEGATIVE_BALANCE',
}

export enum SettlementKycStatus {
  NOT_REQUIRED = 'NOT_REQUIRED',
  PENDING = 'PENDING',
  PASSED = 'PASSED',
  FAILED = 'FAILED',
  ON_HOLD = 'ON_HOLD',
}

export enum SettlementPeriodType {
  CUSTOM = 'CUSTOM',
  LAST_MONTH = 'LAST_MONTH',
  LAST_QUARTER = 'LAST_QUARTER',
  LAST_YEAR = 'LAST_YEAR',
  CURRENT_MONTH = 'CURRENT_MONTH',
  CURRENT_QUARTER = 'CURRENT_QUARTER',
  CURRENT_YEAR = 'CURRENT_YEAR',
}

export const UserTypeStrings = {
  [UserTypes.ADMIN]: 'Admin',
  [UserTypes.BOATER]: 'Boater',
  [UserTypes.MARINA]: 'Marina',
  [UserTypes.REGIONAL_OPERATOR]: 'Region Operator',
  [UserTypes.YACHT_CLUB_ADMIN]: 'Yacht Club Admin',
  [UserTypes.SUB_ADMIN]: 'Sub Admin',
  [UserTypes.DMO_ADMIN]: 'DMO Admin',
};

export function getUserTypeNumber(
  userTypeString: string
): UserTypes | undefined {
  const entry = Object.entries(UserTypeStrings).find(
    ([, value]) => value === userTypeString
  );
  return entry ? parseInt(entry[0]) : undefined;
}

export const activityTypeMap: Partial<
  Record<ActivityLogType, { label: string; variant: string }>
> = {
  [ActivityLogType.LOGIN]: { label: 'Login', variant: 'success' },
  [ActivityLogType.LOGIN_FAILED]: { label: 'Login Failed', variant: 'danger' },
  [ActivityLogType.LOGOUT]: { label: 'Logout', variant: 'light' },
  [ActivityLogType.REGISTER]: { label: 'Register', variant: 'primary' },
  [ActivityLogType.PASSWORD_RESET]: {
    label: 'Password Reset',
    variant: 'info',
  },
  [ActivityLogType.PASSWORD_CHANGE]: {
    label: 'Password Change',
    variant: 'info',
  },
  [ActivityLogType.ACCOUNT_UPDATE]: {
    label: 'Account Update',
    variant: 'warning',
  },
  [ActivityLogType.EMAIL_VERIFICATION_REQUEST]: {
    label: 'Email Verification Request',
    variant: 'warning',
  },
  [ActivityLogType.EMAIL_VERIFIED]: {
    label: 'Email Verified',
    variant: 'success',
  },
  [ActivityLogType.PASSWORD_RESET_REQUEST]: {
    label: 'Password Reset Request',
    variant: 'warning',
  },
  [ActivityLogType.USER_CREATED]: { label: 'User Created', variant: 'primary' },
  [ActivityLogType.USER_UPDATED]: { label: 'User Updated', variant: 'warning' },
  [ActivityLogType.USER_ACTIVATED]: {
    label: 'User Activated',
    variant: 'success',
  },
  [ActivityLogType.USER_DEACTIVATED]: {
    label: 'User Deactivated',
    variant: 'danger',
  },
  [ActivityLogType.USER_DELETED]: { label: 'User Deleted', variant: 'danger' },
  [ActivityLogType.PDF_EXPORT]: { label: 'Export PDF', variant: 'warning' },
  [ActivityLogType.REGISTER_FAILED]: {
    label: 'Registration Failed',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_CREATED]: {
    label: 'Harbor Created',
    variant: 'primary',
  },
  [ActivityLogType.HARBOR_UPDATED]: {
    label: 'Harbor Updated',
    variant: 'warning',
  },
  [ActivityLogType.HARBOR_DELETED]: {
    label: 'Harbor Deleted',
    variant: 'danger',
  },
  [ActivityLogType.FEEDBACK_SUBMITTED]: {
    label: 'Feedback Submitted',
    variant: 'info',
  },
  [ActivityLogType.FEEDBACK_EXPORTED]: {
    label: 'Feedback Exported',
    variant: 'warning',
  },
  [ActivityLogType.FEEDBACK_UPDATED]: {
    label: 'Feedback Updated',
    variant: 'info',
  },
  [ActivityLogType.FEATURED_LISTING_UPDATED]: {
    label: 'Featured Listing Updated',
    variant: 'success',
  },
  [ActivityLogType.ROUTE_CREATED]: {
    label: 'Route Created',
    variant: 'primary',
  },
  [ActivityLogType.BOAT_CREATED]: {
    label: 'Boat Created',
    variant: 'primary',
  },
  [ActivityLogType.BOAT_UPDATED]: {
    label: 'Boat Updated',
    variant: 'warning',
  },
  [ActivityLogType.BOAT_DELETED]: {
    label: 'Boat Deleted',
    variant: 'danger',
  },
  [ActivityLogType.ROUTE_SAVED]: {
    label: 'Route Saved',
    variant: 'primary',
  },
  [ActivityLogType.SEA_ROUTE_CALCULATED]: {
    label: 'Sea Route Calculated',
    variant: 'primary',
  },
  [ActivityLogType.SEA_ROUTE_CREATED]: {
    label: 'Sea Route Created',
    variant: 'primary',
  },
  [ActivityLogType.GDPR_CONSENT]: {
    label: 'GDPR Consent',
    variant: 'primary',
  },
  [ActivityLogType.EXPORT_CONTROL]: {
    label: 'Export Control',
    variant: 'primary',
  },
  [ActivityLogType.CMS_PAGE_UPDATED]: {
    label: 'CMS Updated',
    variant: 'primary',
  },
  [ActivityLogType.CMS_CONTENT_UPDATED]: {
    label: 'CMS Content Updated',
    variant: 'primary',
  },
  [ActivityLogType.CMS_CONTENT_DELETED]: {
    label: 'CMS Content Delete',
    variant: 'danger',
  },
  [ActivityLogType.REGION_CREATED]: {
    label: 'Region Created',
    variant: 'primary',
  },
  [ActivityLogType.REGION_UPDATED]: {
    label: 'Region Updated',
    variant: 'warning',
  },
  [ActivityLogType.REGION_DELETED]: {
    label: 'Region Delete',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_CREATED]: {
    label: 'Club Created',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_UPDATED]: {
    label: 'Club Update',
    variant: 'warning',
  },
  [ActivityLogType.CLUB_DELETED]: {
    label: 'Club Deleted',
    variant: 'danger',
  },
  [ActivityLogType.BERTH_CATEGORY_CREATED]: {
    label: 'Berth Category Created',
    variant: 'primary',
  },
  [ActivityLogType.BERTH_CATEGORY_UPDATED]: {
    label: 'Berth Category Updated',
    variant: 'warning',
  },
  [ActivityLogType.BERTH_CATEGORY_DELETED]: {
    label: 'Berth Category Deleted',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_UPDATED_BY_REGION_OPERATOR]: {
    label: 'Harbor Updated By Region Operator',
    variant: 'warning',
  },
  [ActivityLogType.BERTH_CREATE]: {
    label: 'Berth Created',
    variant: 'primary',
  },
  [ActivityLogType.BERTH_UPDATE]: {
    label: 'Berth Updated',
    variant: 'warning',
  },
  [ActivityLogType.BERTH_DELETE]: {
    label: 'Berth Deleted',
    variant: 'danger',
  },
  [ActivityLogType.BOOKING_CREATE]: {
    label: 'Booking Created',
    variant: 'primary',
  },
  [ActivityLogType.BOOKING_UPDATE]: {
    label: 'Booking Updated',
    variant: 'warning',
  },
  [ActivityLogType.BOOKING_DELETE]: {
    label: 'Booking Deleted',
    variant: 'danger',
  },
  [ActivityLogType.ADDON_CREATE]: {
    label: 'Addon Created',
    variant: 'primary',
  },
  [ActivityLogType.ADDON_UPDATE]: {
    label: 'Addon Updated',
    variant: 'warning',
  },
  [ActivityLogType.ADDON_DELETE]: {
    label: 'Addon Deleted',
    variant: 'danger',
  },
  [ActivityLogType.BERTH_GROUP_UPDATE]: {
    label: 'Berth Group Updated',
    variant: 'warning',
  },
  [ActivityLogType.POI_CREATE]: {
    label: 'POI Created',
    variant: 'primary',
  },
  [ActivityLogType.POI_UPDATE]: {
    label: 'POI Updated',
    variant: 'warning',
  },
  [ActivityLogType.POI_DELETE]: {
    label: 'POI Deleted',
    variant: 'danger',
  },
  [ActivityLogType.COMMUNITY_CREATED]: {
    label: 'Community Created',
    variant: 'primary',
  },
  [ActivityLogType.COMMUNITY_UPDATED]: {
    label: 'Community Updated',
    variant: 'warning',
  },
  [ActivityLogType.COMMUNITY_DELETED]: {
    label: 'Community Deleted',
    variant: 'danger',
  },
  [ActivityLogType.USER_JOINED_COMMUNITY]: {
    label: 'User Joined Community',
    variant: 'primary',
  },
  [ActivityLogType.USER_REQUESTED_COMMUNITY_JOIN]: {
    label: 'User Requested Community Join',
    variant: 'warning',
  },
  [ActivityLogType.COMMUNITY_USERS_LISTED]: {
    label: 'Community Users Listed',
    variant: 'primary',
  },
  [ActivityLogType.COMMUNITY_JOIN_APPROVED]: {
    label: 'Community Join Approved',
    variant: 'primary',
  },
  [ActivityLogType.COMMUNITY_JOIN_REJECTED]: {
    label: 'Community Join Rejected',
    variant: 'danger',
  },
  [ActivityLogType.USER_REMOVED_FROM_COMMUNITY]: {
    label: 'User Removed From Community',
    variant: 'danger',
  },
  [ActivityLogType.POST_CREATED]: {
    label: 'Post Created',
    variant: 'primary',
  },
  [ActivityLogType.POST_UPDATED]: {
    label: 'Post Updated',
    variant: 'warning',
  },
  [ActivityLogType.POST_DELETED]: {
    label: 'Post Deleted',
    variant: 'danger',
  },
  [ActivityLogType.POST_LIKED]: {
    label: 'Post Liked',
    variant: 'primary',
  },
  [ActivityLogType.POST_UNLIKED]: {
    label: 'Post Unliked',
    variant: 'warning',
  },
  [ActivityLogType.POST_REPORTED]: {
    label: 'Post Reported',
    variant: 'danger',
  },
  [ActivityLogType.POST_MODERATED]: {
    label: 'Post Moderated',
    variant: 'primary',
  },
  [ActivityLogType.POST_DOWNLOADED]: {
    label: 'Post Downloaded',
    variant: 'primary',
  },
  [ActivityLogType.BERTH_GROUP_DELETE]: {
    label: 'Berth Group Deleted',
    variant: 'danger',
  },
  [ActivityLogType.COMMENT_CREATED]: {
    label: 'Comment Created',
    variant: 'primary',
  },
  [ActivityLogType.FEEDBACK_ROUTED]: {
    label: 'Feedback Routed',
    variant: 'warning',
  },
  [ActivityLogType.CLUB_APPROVE]: {
    label: 'Club Approved',
    variant: 'success',
  },
  [ActivityLogType.CLUB_REJECT]: {
    label: 'Club Rejected',
    variant: 'danger',
  },
  [ActivityLogType.ORGANIZATION_CREATE]: {
    label: 'Organization Created',
    variant: 'primary',
  },
  [ActivityLogType.ORGANIZATION_UPDATE]: {
    label: 'Organization Updated',
    variant: 'warning',
  },
  [ActivityLogType.ORGANIZATION_DELETE]: {
    label: 'Organization Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_PROVIDER_CREATE]: {
    label: 'Service Provider Created',
    variant: 'primary',
  },
  [ActivityLogType.SERVICE_PROVIDER_UPDATE]: {
    label: 'Service Provider Updated',
    variant: 'warning',
  },
  [ActivityLogType.SERVICE_PROVIDER_DELETE]: {
    label: 'Service Provider Deleted',
    variant: 'danger',
  },
  [ActivityLogType.USER_BLOCKED_IN_COMMUNITY]: {
    label: 'User Blocked In Community',
    variant: 'danger',
  },
  [ActivityLogType.COMMUNITY_JOIN_REQUEST]: {
    label: 'Community Join Request',
    variant: 'primary',
  },
  [ActivityLogType.COMMUNITY_JOIN_REQUEST_UPDATED]: {
    label: 'Community Join Request Updated',
    variant: 'warning',
  },
  [ActivityLogType.BOOKING_CANCEL]: {
    label: 'Booking Cancel',
    variant: 'danger',
  },
  [ActivityLogType.PIER_CREATED]: {
    label: ' Pier Created',
    variant: 'primary',
  },
  [ActivityLogType.PIER_UPDATED]: {
    label: ' Pier Updated',
    variant: 'warning',
  },
  [ActivityLogType.PIER_DELETED]: {
    label: ' Pier Deleted',
    variant: 'danger',
  },
  [ActivityLogType.NOTIFICATION_SENT]: {
    label: 'Notification Sent',
    variant: 'primary',
  },
  [ActivityLogType.NOTIFICATION_READ]: {
    label: 'Notification Read',
    variant: 'warning',
  },
  [ActivityLogType.NOTIFICATION_DELETED]: {
    label: 'Notification Deleted',
    variant: 'danger',
  },
  [ActivityLogType.BERTH_UNAVAILABILITY]: {
    label: 'Berth Unavailability',
    variant: 'primary',
  },
  [ActivityLogType.HARBOR_FAVORITED]: {
    label: 'Harbor Favorited',
    variant: 'primary',
  },
  [ActivityLogType.HARBOR_UNFAVORITED]: {
    label: 'Harbor unfavorited',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_DELETION_REQUESTED]: {
    label: 'Harbor Delete Requested',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_DELETION_APPROVED]: {
    label: 'Harbor Delete Approved',
    variant: 'primary',
  },
  [ActivityLogType.HARBOR_DELETION_REJECTED]: {
    label: 'Harbor Delete Rejected',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_RECOVERED]: {
    label: 'Harbor Recovered',
    variant: 'primary',
  },
  [ActivityLogType.DEPLOYMENT_CONFIRMED]: {
    label: 'Deployment Confirmed',
    variant: 'primary',
  },
  [ActivityLogType.DEPLOYMENT_REJECTED]: {
    label: 'Deployment Rejected',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_ASSOCIATION_REQUESTED]: {
    label: 'Harbor Association Requested',
    variant: 'primary',
  },
  [ActivityLogType.HARBOR_ASSOCIATION_APPROVED]: {
    label: 'Harbor Association Approved',
    variant: 'success',
  },
  [ActivityLogType.HARBOR_ASSOCIATION_REJECTED]: {
    label: 'Harbor Association Rejected',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_ASSOCIATION_CANCELLED]: {
    label: 'Harbor Association Cancelled',
    variant: 'warning',
  },
  [ActivityLogType.HARBOR_ASSOCIATION_REMOVED]: {
    label: 'Harbor Association Removed',
    variant: 'danger',
  },
  [ActivityLogType.FEED_CONTROL_CHANGE]: {
    label: 'Feed Control Changed',
    variant: 'warning',
  },
  [ActivityLogType.SUBSCRIPTION_CREATED]: {
    label: 'Subscription Created',
    variant: 'primary',
  },
  [ActivityLogType.USER_SUBSCRIBED]: {
    label: 'User Subscribed',
    variant: 'success',
  },
  [ActivityLogType.SUBSCRIPTION_CANCELLED]: {
    label: 'Subscription Cancelled',
    variant: 'danger',
  },
  [ActivityLogType.SUBSCRIPTION_AUTO_RENEW_CANCELLED]: {
    label: 'Subscription Auto-Renew Cancelled',
    variant: 'warning',
  },
  [ActivityLogType.AUDIT]: {
    label: 'Audit',
    variant: 'light',
  },
  [ActivityLogType.LIST_VALUE_CREATED]: {
    label: 'List Value Created',
    variant: 'primary',
  },
  [ActivityLogType.LIST_VALUE_UPDATED]: {
    label: 'List Value Updated',
    variant: 'warning',
  },
  [ActivityLogType.LIST_VALUE_DELETED]: {
    label: 'List Value Deleted',
    variant: 'danger',
  },
  [ActivityLogType.HARBOR_FINANCE_REPORT_EXPORTED]: {
    label: 'Harbor Finance Report Exported',
    variant: 'info',
  },
  [ActivityLogType.BOOKING_REPORT_EXPORTED]: {
    label: 'Booking Report Exported',
    variant: 'info',
  },
  [ActivityLogType.ENTITLEMENT_GRANT_CREATED]: {
    label: 'Entitlement Grant Created',
    variant: 'primary',
  },
  [ActivityLogType.ENTITLEMENT_GRANT_UPDATED]: {
    label: 'Entitlement Grant Updated',
    variant: 'warning',
  },
  [ActivityLogType.ENTITLEMENT_GRANT_CANCELLED]: {
    label: 'Entitlement Grant Cancelled',
    variant: 'danger',
  },
  [ActivityLogType.ENTITLEMENT_GRANT_EXPIRED]: {
    label: 'Entitlement Grant Expired',
    variant: 'danger',
  },
  [ActivityLogType.PLAN_CREATED]: {
    label: 'Plan Created',
    variant: 'primary',
  },
  [ActivityLogType.PLAN_UPDATED]: {
    label: 'Plan Updated',
    variant: 'warning',
  },
  [ActivityLogType.PLAN_DELETED]: {
    label: 'Plan Deleted',
    variant: 'danger',
  },
  [ActivityLogType.PLAN_PERMISSIONS_UPDATED]: {
    label: 'Plan Permissions Updated',
    variant: 'warning',
  },
  [ActivityLogType.COUPON_CREATED]: {
    label: 'Coupon Created',
    variant: 'primary',
  },
  [ActivityLogType.COUPON_UPDATED]: {
    label: 'Coupon Updated',
    variant: 'warning',
  },
  [ActivityLogType.COUPON_DELETED]: {
    label: 'Coupon Deleted',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_RESOURCE_CREATED]: {
    label: 'Club Resource Created',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_RESOURCE_UPDATED]: {
    label: 'Club Resource Updated',
    variant: 'warning',
  },
  [ActivityLogType.CLUB_RESOURCE_DELETED]: {
    label: 'Club Resource Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SUBMIT_CONTACT_FORM]: {
    label: 'Contact Form Submitted',
    variant: 'primary',
  },
  [ActivityLogType.YATCH_CLUB_ADMIN_CREATED]: {
    label: 'Yacht Club Admin Created',
    variant: 'primary',
  },
  [ActivityLogType.COMMENT_LIKE]: {
    label: 'Comment Liked',
    variant: 'primary',
  },
  [ActivityLogType.COMMENT_UNLIKE]: {
    label: 'Comment Unliked',
    variant: 'warning',
  },
  [ActivityLogType.SEA_ROUTE_UPDATED]: {
    label: 'Sea Route Updated',
    variant: 'warning',
  },
  [ActivityLogType.TRANSACTION_CREATED]: {
    label: 'Transaction Created',
    variant: 'primary',
  },
  [ActivityLogType.KPI_LOGS]: {
    label: 'KPI Logs',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_SHIFT_CREATED]: {
    label: 'Shift Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_SHIFT_ASSIGNED]: {
    label: 'Shift Assigned',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_SHIFT_UNASSIGNED]: {
    label: 'Shift Unassigned',
    variant: 'warning',
  },
  [ActivityLogType.CLUB_SHIFT_COMPLETED]: {
    label: 'Shift Completed',
    variant: 'success',
  },
  [ActivityLogType.CLUB_RESOURCE_BOOKING_CREATED]: {
    label: 'Club Booking Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_RESOURCE_BOOKING_UPDATED]: {
    label: 'Club Booking Updated',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_RESOURCE_BOOKING_CANCELLED]: {
    label: 'Club Booking Cancelled',
    variant: 'danger',
  },
  [ActivityLogType.RETURNED_KEYS]: {
    label: 'Keys Returned',
    variant: 'success',
  },
  [ActivityLogType.CLUB_INSPECTION_ASSIGNED]: {
    label: 'Club Inspection Assigned',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_INSPECTION_COMPLETED]: {
    label: 'Club Inspection Completed',
    variant: 'success',
  },
  [ActivityLogType.COUPON_VALIDATED]: {
    label: 'Coupon Validated',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_REQUEST_CREATED]: {
    label: 'Service Request Created',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_REQUEST_APPROVED]: {
    label: 'Service Request Approved',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_REQUEST_STATUS_UPDATED]: {
    label: 'Service Request Status Updated',
    variant: 'primary',
  },
  [ActivityLogType.POI_REQUEST_CREATED]: {
    label: 'POI Request Created',
    variant: 'success',
  },
  [ActivityLogType.POI_REQUEST_UPDATED]: {
    label: 'POI Request Updated',
    variant: 'primary',
  },
  [ActivityLogType.POI_REQUEST_DELETED]: {
    label: 'POI Request Deleted',
    variant: 'danger',
  },
  [ActivityLogType.POI_REQUEST_APPROVED]: {
    label: 'POI Request Approved',
    variant: 'success',
  },
  [ActivityLogType.POI_REQUEST_REJECTED]: {
    label: 'POI Request Rejected',
    variant: 'danger',
  },
  [ActivityLogType.POI_REQUEST_VIEWED]: {
    label: 'POI Request Viewed',
    variant: 'primary',
  },
  [ActivityLogType.POI_REQUEST_VIEWED_ALL]: {
    label: 'All POI Requests Viewed',
    variant: 'primary',
  },
  [ActivityLogType.AD_CREATED]: {
    label: 'Advertisement Created',
    variant: 'success',
  },
  [ActivityLogType.AD_UPDATED]: {
    label: 'Advertisement Updated',
    variant: 'primary',
  },
  [ActivityLogType.AD_DELETED]: {
    label: 'Advertisement Deleted',
    variant: 'danger',
  },
  [ActivityLogType.AD_SLOT_CREATED]: {
    label: 'Ad Slot Created',
    variant: 'success',
  },
  [ActivityLogType.AD_SLOT_UPDATED]: {
    label: 'Ad Slot Updated',
    variant: 'primary',
  },
  [ActivityLogType.AD_SLOT_DELETED]: {
    label: 'Ad Slot Deleted',
    variant: 'danger',
  },
  [ActivityLogType.AD_CREATIVE_CREATED]: {
    label: 'Ad Creative Created',
    variant: 'success',
  },
  [ActivityLogType.AD_CREATIVE_UPDATED]: {
    label: 'Ad Creative Updated',
    variant: 'primary',
  },
  [ActivityLogType.AD_CREATIVE_DELETED]: {
    label: 'Ad Creative Deleted',
    variant: 'danger',
  },
  [ActivityLogType.AD_IMPRESSION]: {
    label: 'Ad Impression',
    variant: 'info',
  },
  [ActivityLogType.AD_CLICK]: {
    label: 'Ad Click',
    variant: 'info',
  },
  [ActivityLogType.SERVICE_PROVIDER_CREATED]: {
    label: 'Service Provider Created',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_PROVIDER_UPDATED]: {
    label: 'Service Provider Updated',
    variant: 'primary',
  },
  [ActivityLogType.SERVICE_PROVIDER_DELETED]: {
    label: 'Service Provider Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_PROVIDER_APPROVED]: {
    label: 'Service Provider Approved',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_PROVIDER_REGISTERED]: {
    label: 'Service Provider Registered',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_REQUEST_UPDATED]: {
    label: 'Service Request Updated',
    variant: 'primary',
  },
  [ActivityLogType.SERVICE_REQUEST_REJECTED]: {
    label: 'Service Request Rejected',
    variant: 'danger',
  },
  [ActivityLogType.CREATE_EVENT]: {
    label: 'Community Event Created',
    variant: 'success',
  },
  [ActivityLogType.UPDATE_EVENT]: {
    label: 'Community Event Updated',
    variant: 'primary',
  },
  [ActivityLogType.CANCEL_EVENT]: {
    label: 'Community Event Cancelled',
    variant: 'danger',
  },
  [ActivityLogType.RSVP]: {
    label: 'Community Event RSVP',
    variant: 'info',
  },
  [ActivityLogType.EMAIL_TEMPLATE_UPDATE]: {
    label: 'Email Template Updated',
    variant: 'info',
  },
  [ActivityLogType.NOTIFICATION_TEMPLATE_UPDATE]: {
    label: 'Notification Template Updated',
    variant: 'info',
  },
  [ActivityLogType.SERVICE_BOOKING_CREATED]: {
    label: 'Service Booking Created',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_BOOKING_UPDATED]: {
    label: 'Service Booking Updated',
    variant: 'primary',
  },
  [ActivityLogType.SERVICE_BOOKING_CANCELLED]: {
    label: 'Service Booking Cancelled',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_BOOKING_COMPLETED]: {
    label: 'Service Booking Completed',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_BOOKING_APPROVED]: {
    label: 'Service Booking Approved',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_BOOKING_REJECTED]: {
    label: 'Service Booking Rejected',
    variant: 'danger',
  },
  [ActivityLogType.SUBSCRIPTION_REMAINDER]: {
    label: 'Subscription Remainder',
    variant: 'info',
  },
  [ActivityLogType.SUBSCRIPTION_EXPIRED]: {
    label: 'Subscription Expired',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_CREATED]: {
    label: 'Service Created',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_UPDATED]: {
    label: 'Service Updated',
    variant: 'primary',
  },
  [ActivityLogType.SERVICE_DELETED]: {
    label: 'Service Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_VISIBILITY_UPDATED]: {
    label: 'Service Visibility Updated',
    variant: 'info',
  },
  [ActivityLogType.EVENT_COMMENTED]: {
    label: 'Commented on Event',
    variant: 'success',
  },
  [ActivityLogType.EVENT_COMMENT_LIKED]: {
    label: 'Liked in Event Comment',
    variant: 'success',
  },
  [ActivityLogType.EVENT_COMMENT_UNLIKED]: {
    label: 'Unliked in Event Comment',
    variant: 'primary',
  },
  [ActivityLogType.TEMPLATE_LAYOUT_UPDATE]: {
    label: 'Template Layout Updated',
    variant: 'info',
  },
  [ActivityLogType.MANUAL_ANNOTATION_UPLOADED]: {
    label: 'Manual Annotation Uploaded',
    variant: 'primary',
  },
  [ActivityLogType.MANUAL_ANNOTATION_RETRIEVED]: {
    label: 'Manual Annotation Retrieved',
    variant: 'info',
  },
  [ActivityLogType.MANUAL_ANNOTATION_DELETED]: {
    label: 'Manual Annotation Deleted',
    variant: 'danger',
  },
  [ActivityLogType.MANUAL_ANNOTATION_LISTED]: {
    label: 'Manual Annotations Listed',
    variant: 'info',
  },
  [ActivityLogType.MANUAL_ANNOTATION_PUBLISHED]: {
    label: 'Manual Annotation Published',
    variant: 'success',
  },
  [ActivityLogType.MANUAL_ANNOTATION_EDITER]: {
    label: 'Manual Annotation Editor',
    variant: 'info',
  },
  [ActivityLogType.SYSTEM_ANNOUNCEMENT_CREATED]: {
    label: 'System Announcement Created',
    variant: 'success',
  },
  [ActivityLogType.SYSTEM_ANNOUNCEMENT_UPDATED]: {
    label: 'System Announcement Updated',
    variant: 'primary',
  },
  [ActivityLogType.SYSTEM_ANNOUNCEMENT_DELETED]: {
    label: 'System Announcement Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SETTLEMENT_RUN_CREATED]: {
    label: 'Settlement Run Created',
    variant: 'success',
  },
  [ActivityLogType.SETTLEMENT_RUN_EXPORTED]: {
    label: 'Settlement Run Exported',
    variant: 'info',
  },
  [ActivityLogType.CLUB_BOAT_CREATED]: {
    label: 'Club Boat Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_BOAT_UPDATED]: {
    label: 'Club Boat Updated',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_BOAT_DELETED]: {
    label: 'Club Boat Deleted',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_BOAT_VIEWED]: {
    label: 'Club Boat Viewed',
    variant: 'info',
  },
  [ActivityLogType.CLUB_BOATS_LISTED]: {
    label: 'Club Boats Listed',
    variant: 'info',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_LISTED]: {
    label: 'Club Member Roster Listed',
    variant: 'info',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_VIEWED]: {
    label: 'Club Member Roster Viewed',
    variant: 'info',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_CREATED]: {
    label: 'Club Member Roster Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_UPDATED]: {
    label: 'Club Member Roster Updated',
    variant: 'primary',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_STATUS_UPDATED]: {
    label: 'Club Member Roster Status Updated',
    variant: 'info',
  },
  [ActivityLogType.CLUB_MEMBER_ROSTER_DELETED]: {
    label: 'Club Member Roster Deleted',
    variant: 'danger',
  },
  [ActivityLogType.SERVICE_SUBMITTED]: {
    label: 'Service Submitted',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_WITHDRAWN]: {
    label: 'Service Withdrawn',
    variant: 'danger',
  },
  [ActivityLogType.PRODUCT_VAT_CONFIG_CREATED]: {
    label: 'Product VAT Config Created',
    variant: 'success',
  },
  [ActivityLogType.PRODUCT_VAT_CONFIG_UPDATED]: {
    label: 'Product VAT Config Updated',
    variant: 'primary',
  },
  [ActivityLogType.PRODUCT_VAT_CONFIG_DELETED]: {
    label: 'Product VAT Config Deleted',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_COMMUNITY_CREATED]: {
    label: 'Club Community Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_COMMUNITY_USER_ADDED]: {
    label: 'Club Community User Added',
    variant: 'success',
  },
  [ActivityLogType.CLUB_COMMUNITY_USER_REMOVED]: {
    label: 'Club Community User Removed',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_COMMUNITY_ACCESS_GRANTED]: {
    label: 'Club Community Access Granted',
    variant: 'success',
  },
  [ActivityLogType.CLUB_COMMUNITY_ACCESS_DENIED]: {
    label: 'Club Community Access Denied',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_COMMUNITY_SYNC_SUCCESS]: {
    label: 'Club Community Sync Success',
    variant: 'success',
  },
  [ActivityLogType.CLUB_COMMUNITY_SYNC_FAILED]: {
    label: 'Club Community Sync Failed',
    variant: 'danger',
  },
  [ActivityLogType.CLUB_FORUM_POST_CREATED]: {
    label: 'Club Forum Post Created',
    variant: 'success',
  },
  [ActivityLogType.CLUB_FORUM_ACCESS_ATTEMPT]: {
    label: 'Club Forum Access Attempt',
    variant: 'info',
  },
  [ActivityLogType.ARTICLE_CREATED]: {
    label: 'Article Created',
    variant: 'success',
  },

  [ActivityLogType.ARTICLE_UPDATED]: {
    label: 'Article Updated',
    variant: 'info',
  },

  [ActivityLogType.ARTICLE_DELETED]: {
    label: 'Article Deleted',
    variant: 'error',
  },

  [ActivityLogType.ARTICLE_VIEWED]: {
    label: 'Article Viewed',
    variant: 'info',
  },

  [ActivityLogType.ARTICLES_LISTED]: {
    label: 'Articles Listed',
    variant: 'info',
  },

  [ActivityLogType.ARTICLE_STATUS_UPDATED]: {
    label: 'Article Status Updated',
    variant: 'warning',
  },

  [ActivityLogType.ARTICLE_VERSION_PUBLISHED]: {
    label: 'Article Version Published',
    variant: 'success',
  },

  [ActivityLogType.ARTICLE_VERSIONS_LISTED]: {
    label: 'Article Versions Listed',
    variant: 'info',
  },

  [ActivityLogType.ARTICLE_IMAGE_UPLOADED]: {
    label: 'Article Image Uploaded',
    variant: 'success',
  },

  [ActivityLogType.ARTICLE_TRANSLATION_GENERATED]: {
    label: 'Article Translation Generated',
    variant: 'success',
  },

  [ActivityLogType.FAQ_CREATED]: {
    label: 'FAQ Created',
    variant: 'success',
  },
  [ActivityLogType.FAQ_UPDATED]: {
    label: 'FAQ Updated',
    variant: 'info',
  },
  [ActivityLogType.FAQ_DELETED]: {
    label: 'FAQ Deleted',
    variant: 'error',
  },
  [ActivityLogType.FAQ_HELPFUL_UPDATED]: {
    label: 'FAQ Helpful Updated',
    variant: 'success',
  },
  [ActivityLogType.FAQ_TRANSLATION_GENERATED]: {
    label: 'FAQ Translation Generated',
    variant: 'success',
  },
  [ActivityLogType.FAQ_DRAFT_SAVED]: {
    label: 'FAQ Draft Saved',
    variant: 'warning',
  },
  [ActivityLogType.FAQ_TRANSLATION_PUBLISHED]: {
    label: 'FAQ Translation Published',
    variant: 'success',
  },
  [ActivityLogType.FAQ_TRANSLATION_ROLLED_BACK]: {
    label: 'FAQ Translation Rolled Back',
    variant: 'danger',
  },
  [ActivityLogType.THEME_ROUTE_CREATED]: {
    label: 'Theme Route Created',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_UPDATED]: {
    label: 'Theme Route Updated',
    variant: 'info',
  },
  [ActivityLogType.THEME_ROUTE_DELETED]: {
    label: 'Theme Route Deleted',
    variant: 'error',
  },
  [ActivityLogType.THEME_ROUTE_PUBLISHED]: {
    label: 'Theme Route Published',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_VIEWED]: {
    label: 'Theme Route Viewed',
    variant: 'info',
  },
  [ActivityLogType.THEME_ROUTES_LISTED]: {
    label: 'Theme Routes Listed',
    variant: 'info',
  },
  [ActivityLogType.THEME_ROUTE_APPROVAL_REQUESTED]: {
    label: 'Theme Route Approval Requested',
    variant: 'warning',
  },
  [ActivityLogType.THEME_ROUTE_APPROVED]: {
    label: 'Theme Route Approved',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_REJECTED]: {
    label: 'Theme Route Rejected',
    variant: 'error',
  },
  [ActivityLogType.THEME_ROUTE_REVERTED]: {
    label: 'Theme Route Reverted',
    variant: 'warning',
  },
  [ActivityLogType.THEME_ROUTE_UNLOCKED]: {
    label: 'Theme Route Unlocked',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_COMMENT_ADDED]: {
    label: 'Theme Route Comment Added',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_COMMENT_UPDATED]: {
    label: 'Theme Route Comment Updated',
    variant: 'success',
  },
  [ActivityLogType.THEME_ROUTE_COMMENT_DELETED]: {
    label: 'Theme Route Comment Deleted',
    variant: 'error',
  },
  [ActivityLogType.PLATFORM_SETTING_CREATED]: {
    label: 'Platform Setting Created',
    variant: 'success',
  },
  [ActivityLogType.PLATFORM_SETTING_UPDATED]: {
    label: 'Platform Setting Updated',
    variant: 'info',
  },
  [ActivityLogType.PLATFORM_SETTING_GENERATED]: {
    label: 'Platform Setting Generated',
    variant: 'success',
  },
  [ActivityLogType.TESTIMONIAL_CREATED]: {
    label: 'Testimonial Created',
    variant: 'success',
  },
  [ActivityLogType.TESTIMONIAL_UPDATED]: {
    label: 'Testimonial Updated',
    variant: 'info',
  },
  [ActivityLogType.TESTIMONIAL_DELETED]: {
    label: 'Testimonial Deleted',
    variant: 'error',
  },
  [ActivityLogType.TESTIMONIAL_TRANSLATION_GENERATED]: {
    label: 'Testimonial Translation Generated',
    variant: 'success',
  },
  [ActivityLogType.DMO_REGISTRATION_INVITATION_CREATED]: {
    label: 'DMO Registration Invitation Created',
    variant: 'success',
  },
  [ActivityLogType.DMO_REGISTRATION_INVITATION_CANCELLED]: {
    label: 'DMO Registration Invitation Cancelled',
    variant: 'warning',
  },
  [ActivityLogType.DMO_REGISTRATION_INVITATIONS_LISTED]: {
    label: 'DMO Registration Invitations Listed',
    variant: 'info',
  },
  [ActivityLogType.DMO_REGISTRATION_INVITATION_VIEWED]: {
    label: 'DMO Registration Invitation Viewed',
    variant: 'info',
  },
  [ActivityLogType.DMO_REGISTRATION_REQUEST_CREATED]: {
    label: 'DMO Registration Request Created',
    variant: 'success',
  },
  [ActivityLogType.DMO_REGISTRATION_REQUESTS_LISTED]: {
    label: 'DMO Registration Requests Listed',
    variant: 'info',
  },
  [ActivityLogType.DMO_REGISTRATION_REQUEST_VIEWED]: {
    label: 'DMO Registration Request Viewed',
    variant: 'info',
  },
  [ActivityLogType.DMO_REGISTRATION_REQUEST_DELETED]: {
    label: 'DMO Registration Request Deleted',
    variant: 'error',
  },
  [ActivityLogType.DMO_REGISTRATION_TOKEN_VALIDATED]: {
    label: 'DMO Registration Token Validated',
    variant: 'success',
  },
  [ActivityLogType.ROUTING_DIAGNOSTICS]: {
    label: 'Routing Diagnosis',
    variant: 'info',
  },
  [ActivityLogType.NOTIFICATION_RULE_UPDATED]: {
    label: 'Notification rule updated',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_APPROVED]: {
    label: 'Service Approved',
    variant: 'success',
  },
  [ActivityLogType.SERVICE_REJECTED]: {
    label: 'Service Rejected',
    variant: 'error',
  },
  [ActivityLogType.SERVICE_MODERATED]: {
    label: 'Service Moderated',
    variant: 'warning',
  },
  [ActivityLogType.COMMISSION_POLICY_CREATED]: {
    label: 'Commission Policy Created',
    variant: 'success',
  },
  [ActivityLogType.COMMISSION_POLICY_UPDATED]: {
    label: 'Commission Policy Updated',
    variant: 'info',
  },
  [ActivityLogType.COMMISSION_POLICY_DEACTIVATED]: {
    label: 'Commission Policy Deactivated',
    variant: 'error',
  },
  [ActivityLogType.KYC_CREATED]: {
    label: 'KYC Created',
    variant: 'success',
  },
  [ActivityLogType.KYC_UPDATED]: {
    label: 'KYC Updated',
    variant: 'info',
  },
  [ActivityLogType.KYC_STATUS_UPDATED]: {
    label: 'KYC Status Updated',
    variant: 'warning',
  },
  [ActivityLogType.KYC_APPROVED]: {
    label: 'KYC Approved',
    variant: 'success',
  },
  [ActivityLogType.KYC_REJECTED]: {
    label: 'KYC Rejected',
    variant: 'error',
  },
  [ActivityLogType.EXTERNAL_SERVICE_UPDATED]: {
    label: 'External Service Updated',
    variant: 'info',
  },
  [ActivityLogType.TRIAL_STARTED]: {
    label: 'Trial Started',
    variant: 'success',
  },
  [ActivityLogType.TRIAL_CANCELLED]: {
    label: 'Trial Cancelled',
    variant: 'error',
  },
  [ActivityLogType.TRIAL_CONVERSION_ATTEMPTED]: {
    label: 'Trial Conversion Attempted',
    variant: 'warning',
  },
  [ActivityLogType.TRIAL_CONVERTED]: {
    label: 'Trial Converted',
    variant: 'success',
  },
  [ActivityLogType.TRIAL_EXPIRED]: {
    label: 'Trial Expired',
    variant: 'error',
  },
  [ActivityLogType.TRIAL_CONVERSION_FAILED]: {
    label: 'Trial Conversion Failed',
    variant: 'error',
  },
  [ActivityLogType.TRIAL_ACTIVATED]: {
    label: 'Trial Activated',
    variant: 'success',
  },
  [ActivityLogType.BUSINESS_VAT_SET]: {
    label: 'Business Vat Set',
    variant: 'primary',
  },
  [ActivityLogType.BUSINESS_VAT_UPDATE]: {
    label: 'Business Vat Updated',
    variant: 'warning',
  },
  [ActivityLogType.BUSINESS_VAT_REMOVED]: {
    label: 'Business Vat Removed',
    variant: 'danger',
  },
  [ActivityLogType.EXTERNAL_RESERVATION_CREATED]: {
    label: 'External Reservation Created',
    variant: 'primary',
  },
  [ActivityLogType.EXTERNAL_RESERVATION_UPDATED]: {
    label: 'External Reservation Updated',
    variant: 'warning',
  },
  [ActivityLogType.EXTERNAL_RESERVATION_RELEASED]: {
    label: 'External Reservation Released',
    variant: 'danger',
  },
  [ActivityLogType.ICON_IMAGE_CREATED]: {
    label: 'Icon Image Created',
    variant: 'primary',
  },
  [ActivityLogType.ICON_IMAGE_UPDATED]: {
    label: 'Icon Image Updated',
    variant: 'warning',
  },
  [ActivityLogType.ICON_IMAGE_RENAMED]: {
    label: 'Icon Image Renamed',
    variant: 'info',
  },
  [ActivityLogType.ICON_IMAGE_RETIRED]: {
    label: 'Icon Image Retired',
    variant: 'warning',
  },
  [ActivityLogType.ICON_IMAGE_REACTIVATED]: {
    label: 'Icon Image Reactivated',
    variant: 'success',
  },
  [ActivityLogType.ICON_IMAGE_DELETED]: {
    label: 'Icon Image Deleted',
    variant: 'danger',
  },
  [ActivityLogType.GUEST_BOOKING_GLOBAL_SETTING_UPDATED]: {
    label: 'Guest Booking Global Setting Updated',
    variant: 'info',
  },
  [ActivityLogType.GUEST_BOOKING_HARBOR_OVERRIDE_UPDATED]: {
    label: 'Guest Booking Harbor Override Updated',
    variant: 'info',
  },
  [ActivityLogType.GUEST_BOOKING_ACCESS_REQUESTED]: {
    label: 'Guest Booking Access Requested',
    variant: 'info',
  },
  [ActivityLogType.GUEST_BOOKING_CLAIMED]: {
    label: 'Guest Booking Claimed',
    variant: 'success',
  },
  [ActivityLogType.GUEST_BOOKING_CLAIM_REJECTED]: {
    label: 'Guest Booking Claim Rejected',
    variant: 'danger',
  },
  [ActivityLogType.SYNTHETIC_BOOKING_CANARY_ALERT_EMAILS_UPDATED]: {
    label: 'Synthetic Booking Canary Alert Emails Updated',
    variant: 'info',
  },
  [ActivityLogType.SYNTHETIC_BOOKING_CANARY_ENABLED_UPDATED]: {
    label: 'Synthetic Booking Canary Enabled Updated',
    variant: 'info',
  },
  [ActivityLogType.SYNTHETIC_BOOKING_CANARY_INTERVAL_MINUTES_UPDATED]: {
    label: 'Synthetic Booking Canary Interval Minutes Updated',
    variant: 'info',
  },
};

export const categoryOptions = [
  {
    value: 'historic_trade_harbour',
    label: 'HARBOR.CATEGORY.historic_trade_harbour',
  },
  { value: 'industrial_harbour', label: 'HARBOR.CATEGORY.industrial_harbour' },
  {
    value: 'municipal_guest_harbour',
    label: 'HARBOR.CATEGORY.municipal_guest_harbour',
  },
  { value: 'nature_harbour', label: 'HARBOR.CATEGORY.nature_harbour' },
  { value: 'private_harbour', label: 'HARBOR.CATEGORY.private_harbour' },
  {
    value: 'seasonal_service_harbour',
    label: 'HARBOR.CATEGORY.seasonal_service_harbour',
  },
  { value: 'shelter_bay', label: 'HARBOR.CATEGORY.shelter_bay' },
  { value: 'visiting_harbour', label: 'HARBOR.CATEGORY.visiting_harbour' },
  { value: 'club_harbour', label: 'HARBOR.CATEGORY.club_harbour' },
  { value: 'commercial_marina', label: 'HARBOR.CATEGORY.commercial_marina' },
  { value: 'emergency_harbour', label: 'HARBOR.CATEGORY.emergency_harbour' },
  { value: 'guest_harbour', label: 'HARBOR.CATEGORY.guest_harbour' },
  { value: 'fishing_harbour', label: 'HARBOR.CATEGORY.fishing_harbour' },
];

export const servicesOptions = [
  {
    value: 'septic_emptying_station',
    label: 'HARBOR.SERVICE.SEPTIC_EMPTYING_STATION',
  },
  { value: 'taxi_stop', label: 'HARBOR.SERVICE.TAXI_STOP' },
  { value: 'airport', label: 'HARBOR.SERVICE.AIRPORT' },
  { value: 'atm', label: 'HARBOR.SERVICE.ATM' },
  { value: 'bike_rental', label: 'HARBOR.SERVICE.BIKE_RENTAL' },
  { value: 'bus_terminal', label: 'HARBOR.SERVICE.BUS_TERMINAL' },
  { value: 'farm_shop', label: 'HARBOR.SERVICE.FARM_SHOP' },
  { value: 'fuel_station', label: 'HARBOR.SERVICE.FUEL_STATION' },
  { value: 'grocery_store', label: 'HARBOR.SERVICE.GROCERY_STORE' },
  { value: 'hardware_store', label: 'HARBOR.SERVICE.HARDWARE_STORE' },
  { value: 'health_care_center', label: 'HARBOR.SERVICE.HEALTH_CARE_CENTER' },
  { value: 'laundromat', label: 'HARBOR.SERVICE.LAUNDROMAT' },
  { value: 'pharmacy', label: 'HARBOR.SERVICE.PHARMACY' },
  { value: 'post_office', label: 'HARBOR.SERVICE.POST_OFFICE' },
  { value: 'railway_station', label: 'HARBOR.SERVICE.RAILWAY_STATION' },
  { value: 'restaurant', label: 'HARBOR.SERVICE.RESTAURANT' },
];

export const facilityOptions = [
  { value: 'showers', label: 'HARBOR.FACILITY.SHOWERS' },
  { value: 'wifi', label: 'HARBOR.FACILITY.WIFI' },
  { value: 'sauna', label: 'HARBOR.FACILITY.SAUNA' },
  { value: 'toilets', label: 'HARBOR.FACILITY.TOILETS' },
  { value: 'laundry', label: 'HARBOR.FACILITY.LAUNDRY_SERVICE' },
  { value: 'bbq_place', label: 'HARBOR.FACILITY.BBQ_AREA' },
  { value: 'picnic_tables', label: 'HARBOR.FACILITY.PICNIC_TABLES_SEATING' },
  {
    value: 'chemical_wc_disposal',
    label: 'HARBOR.FACILITY.CHEMICAL_WC_DISPOSAL',
  },
  {
    value: 'drinking_water_refill',
    label: 'HARBOR.FACILITY.DRINKING_WATER_REFILL',
  },
  { value: 'electricity_pier', label: 'HARBOR.FACILITY.ELECTRICITY_PIER' },
  { value: 'fresh_water_pier', label: 'HARBOR.FACILITY.FRESH_WATER_PIER' },
  { value: 'kitchen', label: 'HARBOR.FACILITY.KITCHEN' },
  { value: 'recycling', label: 'HARBOR.FACILITY.RECYCLING' },
  { value: 'waste_point', label: 'HARBOR.FACILITY.WASTE_POINT' },
];

// Facility condition options
export enum FacilityCondition {
  EXCELLENT = 'Excellent',
  GOOD = 'Good',
  NEEDS_MAINTENANCE = 'Needs Maintenance',
  OUT_OF_ORDER = 'Out of Order',
}

export const facilityConditionOptions = [
  {
    value: FacilityCondition.EXCELLENT,
    label: 'HARBOR.FACILITY.CONDITION.EXCELLENT',
  },
  { value: FacilityCondition.GOOD, label: 'HARBOR.FACILITY.CONDITION.GOOD' },
  {
    value: FacilityCondition.NEEDS_MAINTENANCE,
    label: 'HARBOR.FACILITY.CONDITION.NEEDS_MAINTENANCE',
  },
  {
    value: FacilityCondition.OUT_OF_ORDER,
    label: 'HARBOR.FACILITY.CONDITION.OUT_OF_ORDER',
  },
];

export const facilityConditionColors = {
  [FacilityCondition.EXCELLENT]:
    'bg-green-100 dark:bg-green-900 contrast:bg-green-900 text-green-800 dark:text-green-200 contrast:text-green-200 border-green-200 dark:border-green-700 contrast:border-green-700',
  [FacilityCondition.GOOD]:
    'bg-blue-100 dark:bg-blue-900 contrast:bg-blue-900 text-blue-800 dark:text-blue-200 contrast:text-blue-200 border-blue-200 dark:border-blue-700 contrast:border-blue-700',
  [FacilityCondition.NEEDS_MAINTENANCE]:
    'bg-yellow-100 dark:bg-yellow-900 contrast:bg-yellow-900 text-yellow-800 dark:text-yellow-200 contrast:text-yellow-200 border-yellow-200 dark:border-yellow-700 contrast:border-yellow-700',
  [FacilityCondition.OUT_OF_ORDER]:
    'bg-red-100 dark:bg-red-900 contrast:bg-red-900 text-red-800 dark:text-red-200 contrast:text-red-200 border-red-200 dark:border-red-700 contrast:border-red-900',
};

export enum AmenityCondition {
  EXCELLENT = 'Excellent',
  GOOD = 'Good',
  NEEDS_MAINTENANCE = 'Needs Maintenance',
  OUT_OF_ORDER = 'Out of Order',
}

export const amenityConditionOptions = [
  {
    value: AmenityCondition.EXCELLENT,
    label: 'HARBOR.FACILITY.CONDITION.EXCELLENT',
  },
  { value: AmenityCondition.GOOD, label: 'HARBOR.FACILITY.CONDITION.GOOD' },
  {
    value: AmenityCondition.NEEDS_MAINTENANCE,
    label: 'HARBOR.FACILITY.CONDITION.NEEDS_MAINTENANCE',
  },
  {
    value: AmenityCondition.OUT_OF_ORDER,
    label: 'HARBOR.FACILITY.CONDITION.OUT_OF_ORDER',
  },
];

export enum HarborType {
  GUEST = 'guest_harbor',
  NATURE = 'nature_harbor',
  CLUB_ASSOCIATION = 'club_association_harbor',
  PRIVATE_OTHER = 'private_other_harbor',
}

export const harborTypeOptions = [
  { value: HarborType.GUEST, label: 'Guest harbour' },
  { value: HarborType.NATURE, label: 'Nature harbour' },
  { value: HarborType.CLUB_ASSOCIATION, label: 'Club/association harbour' },
  { value: HarborType.PRIVATE_OTHER, label: 'Private/other harbor' },
];

/**
 * Shared Saaristo.online harbour timezone selection.
 * Values are IANA identifiers (not fixed UTC offsets). Keep in sync with
 * backend SUPPORTED_HARBOR_TIMEZONES in timezone.util.ts.
 */
export const TIMEZONE_OPTIONS = [
  { value: 'Europe/Helsinki', label: 'Europe/Helsinki (Finland)' },
  { value: 'Europe/Stockholm', label: 'Europe/Stockholm (Sweden)' },
  { value: 'Europe/Tallinn', label: 'Europe/Tallinn (Estonia)' },
  { value: 'Europe/Riga', label: 'Europe/Riga (Latvia)' },
  { value: 'Europe/Vilnius', label: 'Europe/Vilnius (Lithuania)' },
  { value: 'Europe/Berlin', label: 'Europe/Berlin (Germany)' },
  { value: 'Europe/Warsaw', label: 'Europe/Warsaw (Poland)' },
  { value: 'Europe/Copenhagen', label: 'Europe/Copenhagen (Denmark)' },
];

export const amenityConditionColors = {
  [AmenityCondition.EXCELLENT]: 'bg-green-100 text-green-800 border-green-200',
  [AmenityCondition.GOOD]: 'bg-blue-100 text-blue-800 border-blue-200',
  [AmenityCondition.NEEDS_MAINTENANCE]:
    'bg-yellow-100 text-yellow-800 border-yellow-200',
  [AmenityCondition.OUT_OF_ORDER]: 'bg-red-100 text-red-800 border-red-200',
};
// export const amenityOptions = [
//   "Showers",
//   "Laundry",
//   "Wi-Fi",
//   "Restaurant",
//   "Cafe",
// ];

export const navigationAidsOptions = [
  {
    value: 'cardinal_south_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_SOUTH_BOTTOM',
  },
  { value: 'cardinal_south', label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_SOUTH' },
  {
    value: 'cardinal_west_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_WEST_BOTTOM',
  },
  { value: 'cardinal_west', label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_WEST' },
  {
    value: 'cardinal_east_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_EAST_BOTTOM',
  },
  { value: 'cardinal_east', label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_EAST' },
  {
    value: 'cardinal_north_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_NORTH_BOTTOM',
  },
  { value: 'cardinal_north', label: 'HARBOR.NAVIGATION_AIDS.CARDINAL_NORTH' },
  { value: 'lateral_port', label: 'HARBOR.NAVIGATION_AIDS.LATERAL_PORT' },
  {
    value: 'lateral_port_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.LATERAL_PORT_BOTTOM',
  },
  {
    value: 'lateral_starboard',
    label: 'HARBOR.NAVIGATION_AIDS.LATERAL_STARBOARD',
  },
  {
    value: 'lateral_starboard_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.LATERAL_STARBOARD_BOTTOM',
  },
  {
    value: 'special_buoy_pipeline_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.SPECIAL_BUOY_PIPELINE_BOTTOM',
  },
  {
    value: 'special_buoy_pipeline',
    label: 'HARBOR.NAVIGATION_AIDS.SPECIAL_BUOY_PIPELINE',
  },
  {
    value: 'safe_water_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.SAFE_WATER_BOTTOM',
  },
  { value: 'safe_water', label: 'HARBOR.NAVIGATION_AIDS.SAFE_WATER' },
  {
    value: 'isolated_danger_bottom',
    label: 'HARBOR.NAVIGATION_AIDS.ISOLATED_DANGER_BOTTOM',
  },
  { value: 'isolated_danger', label: 'HARBOR.NAVIGATION_AIDS.ISOLATED_DANGER' },
  { value: 'radar_reflector', label: 'HARBOR.NAVIGATION_AIDS.RADAR_REFLECTOR' },
  {
    value: 'lighthouse_major',
    label: 'HARBOR.NAVIGATION_AIDS.LIGHTHOUSE_MAJOR',
  },
  { value: 'leading_line', label: 'HARBOR.NAVIGATION_AIDS.LEADING_LINE' },
  {
    value: 'fairway_edge_mark',
    label: 'HARBOR.NAVIGATION_AIDS.FAIRWAY_EDGE_MARK',
  },
  { value: 'daymark_cairn', label: 'HARBOR.NAVIGATION_AIDS.DAYMARK Cairn' },
];

const vitePublicBase = import.meta.env.BASE_URL.endsWith('/')
  ? import.meta.env.BASE_URL
  : `${import.meta.env.BASE_URL}/`;

/** Self-hosted TinyMCE entry (see `postinstall` script; files live under `public/tinymce`). */
export const TINYMCE_SCRIPT_SRC = `${vitePublicBase}tinymce/tinymce.min.js`;

/**
 * TinyMCE puts `aria-label` on the editor iframe's <body>, but <body> has no
 * role that permits ARIA naming (and no valid role can be added to it).
 * Remove it; the iframe's own title still names the editor.
 */
export const fixTinyMceBodyAria = (editor: {
  on: (event: string, callback: () => void) => void;
  getContainer: () => HTMLElement | null;
}) => {
  editor.on('init', () => {
    const body = editor.getContainer()?.querySelector('iframe')?.contentDocument
      ?.body;
    body?.removeAttribute('aria-label');
  });
};

export const tinymceInit = {
  base_url: `${vitePublicBase}tinymce`,
  suffix: '.min',
  height: 350,
  menubar: false,
  skin: 'oxide',
  plugins: [
    'advlist',
    'autolink',
    'lists',
    'link',
    'image',
    'charmap',
    'preview',
    'anchor',
    'searchreplace',
    'visualblocks',
    'code',
    'fullscreen',
    'insertdatetime',
    'media',
    'table',
    'code',
    'help',
    'wordcount',
  ],
  toolbar: [
    // Main formatting row
    'undo redo | blocks | bold italic forecolor | alignleft aligncenter alignright alignjustify',
    // List and help row (without outdent/indent)
    'bullist numlist | removeformat | help',
    // Indent/outdent row
    'outdent indent',
    // Table tools row (all table controls together)
    'table tabledelete | tableprops tablerowprops tablecellprops | tableinsertrowbefore tableinsertrowafter tabledeleterow | tableinsertcolbefore tableinsertcolafter tabledeletecol | tablemergecells tablesplitcells',
  ],
  image_title: true,
  /* enable automatic uploads of images represented by blob or data URIs*/
  automatic_uploads: true,
  /*
    URL of our upload handler (for more details check: https://www.tiny.cloud/docs/configure/file-image-upload/#images_upload_url)
    images_upload_url: 'postAcceptor.php',
    here we add custom filepicker only to Image dialog
  */
  file_picker_types: 'image',
  typeahead_urls: false,
  image_advtab: false,
  promotion: false,
  onboarding: false,
  branding: false,
  statusbar: false,
  content_style:
    'body { font-family:Helvetica,Arial,sans-serif; font-size:14px }',
  setup: fixTinyMceBodyAria,
};

export enum KpiType {
  ROUTE = 1,
  POI = 2,
  BOOKING = 3,
}

export enum KpiEventType {
  ROUTE_VIEW = 1,
  ROUTE_SAVED = 2,
  POI_VIEW = 3,
  POI_SAVED = 4,
  BOOKING_VIEW = 5,
  BOOKING_ATTEMPT = 6,
  BOOKING_CONFIRMED = 7,
  BOOKING_CANCELLED = 8,
}

export enum InterfaceType {
  ADMIN = 1,
  FRONTEND = 2,
  BACKEND = 3,
}

export enum Environments {
  LOCAL = 'local',
  DEVELOPMENT = 'development',
  STAGING = 'staging',
  PRODUCTION = 'production',
  TEST = 'test',
}

export enum ManualDeploymentStatus {
  CONFIRMED = 1,
  FAILED = 2,
}

export enum ServiceProviderStatus {
  ACTIVE = 1,
  ARCHIVED = 2,
}

export enum BusinessType {
  COMPANY = 1,
  SOLE_TRADER = 2,
}

// Campaign status for Advertisements
export enum CampaignStatus {
  DRAFT = 'draft',
  ACTIVE = 'active',
  PAUSED = 'paused',
  COMPLETED = 'completed',
  CANCELLED = 'cancelled',
}

export enum SubscriptionPlanLevel {
  P1 = 'P1',
  P2 = 'P2',
  P3 = 'P3',
}

export enum KYCType {
  HARBOR = 'harbor',
  CLUB = 'club',
  SERVICE_PROVIDER = 'service_provider',
}

export enum KYCStatus {
  PENDING = 'pending',
  UNDER_REVIEW = 'under_review',
  APPROVED = 'approved',
  REJECTED = 'rejected',
  REQUIRES_UPDATE = 'requires_update',
  NOT_REQUIRED = 'not_required',
  ON_HOLD = 'on_hold',
  RESUBMITTED = 'resubmitted',
  DRAFT = 'draft',
}
